import type { ChipTone } from '../../components/Chip';
import type { BulkResultItem } from './BulkResult';
import { stateChip } from '../qrs/states';
import type { QrState } from '../qrs/types';

export interface Org {
  id: string;
  name: string | null;
  slug: string;
  type: 'partner' | 'personal';
  status: 'active' | 'blocked' | string;
  createdAt: string | null;
}

export interface ArtUsage {
  total: number;
  fromPool: number;
  generated: number;
}

export interface OrgUsageRow {
  orgId: string;
  name: string | null;
  type: string;
  status: string;
  month: string;
  qrArt: ArtUsage;
}

export interface OrgUsage {
  month: string;
  source: 'aggregates' | 'slots_scan';
  items: OrgUsageRow[];
}

export interface SecuritySlot {
  id: string;
  code: string | null;
  domain: string;
  orgId: string | null;
  state: string | null;
  suspension: { reason: string | null; security: boolean; at: string | null } | null;
  changed: boolean;
}

export interface VaultItem {
  hitId: string;
  ip: string;
  code: string | null;
  domain: string | null;
  orgId: string | null;
  createdAt: string | null;
  expireAt: string | null;
}

export interface VaultResult {
  items: VaultItem[];
  missing: { hitId: string; reason: string }[];
  count: number;
}

export interface Domain {
  name: string;
  primary: boolean;
  active: boolean;
  docPrefix: string;
  allowedOrgIds: string[];
  updatedAt: string | null;
}

export type ReservedCategory = 'banco' | 'organismo' | 'marca' | 'premium';

export interface ReservedName {
  name: string;
  category: ReservedCategory | null;
  addedBy: string | null;
  addedAt: string | null;
}

export interface UnassignedSlot {
  id: string;
  code: string;
  domain: string;
  type: string | null;
  kind: string;
  clientId: string | null;
  active: boolean;
  hits: number;
  createdAt: string | null;
}

export const CATEGORY_LABEL: Record<ReservedCategory, string> = {
  banco: 'Banco',
  organismo: 'Organismo',
  marca: 'Marca',
  premium: 'Premium',
};

export function currentMonth(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function previousMonths(count: number, now = new Date()): string[] {
  return Array.from({ length: count }, (_, i) => currentMonth(new Date(now.getFullYear(), now.getMonth() - i, 1)));
}

/** Tipo de imagen del QR (R14). */
export type QrImageType = 'classic' | 'styled' | 'art';

/** `GET /v1/operator/qrs` (R14.2): un link de cualquier organización. */
export interface OperatorQrItem {
  id: string;
  code: string;
  domain: string;
  orgId: string | null;
  orgName: string | null;
  qrType: QrImageType;
  state: string | null;
  kind: string | null;
  lotId: string | null;
  targetUrl: string | null;
  externalRef: string | null;
  businessName: string | null;
  scannable: boolean | null;
  hasFallback: boolean;
  createdAt: string | null;
  activatedBy: string | null;
  artEngine: string | null;
  artAttempts: number | null;
  artGenSeconds: number | null;
  prompt: string | null;
  stylePreset: string | null;
}

export interface OperatorQrHistoryEntry {
  at: string | null;
  action: string | null;
  before: string | null;
  after: string | null;
  actor: string | { type?: string; id?: string; email?: string; name?: string } | null;
}

export interface OperatorQrDetail extends OperatorQrItem {
  history: OperatorQrHistoryEntry[];
}

export const QR_TYPE_TONE: Record<QrImageType, ChipTone> = { classic: 'info', styled: 'premium', art: 'art' };

export function stateChipFor(state: string | null): { label: string; tone: ChipTone } {
  if (!state) return { label: 'Sin estado', tone: 'neutral' };
  const chip = stateChip({ state: state as QrState });
  return chip.label ? chip : { label: state, tone: 'neutral' };
}

export const QR_TYPE_LABEL: Record<QrImageType, string> = { classic: 'Clásico', styled: 'Con estilo', art: 'QR Art' };

/** Unidad de la cuarentena en `config/operations` (R4.1b). */
export type QuarantineUnit = 'months' | 'days';

export const QUARANTINE_UNIT_LABEL: Record<QuarantineUnit, string> = { months: 'meses', days: 'días' };

/** `GET`/`PUT /v1/operator/config` (R4.1b, R2.1). */
export interface OpsConfig {
  quarantine: { value: number; unit: QuarantineUnit };
  artFreeRegens: number;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/** Respuesta del lote: con cola llega `pendiente` y el resultado se consulta después (R3.4). */
export type BulkResponse = {
  bulkId?: string;
  status?: 'pendiente' | 'corriendo' | 'listo';
  counts?: { done: number; skipped: number; failed: number };
  items?: BulkResultItem[];
};
